import { COLORS } from '../lib/theme'
import { Icon } from './Icon'

export interface TeamAccessDeniedProps {
  /** Return to the teams list. */
  onBack?: () => void
  /**
   * Optional "join with a code" CTA (CLUB-177). Rendered only when provided so
   * this component doesn't imply a join flow that isn't wired up yet.
   */
  onJoinWithCode?: () => void
}

/**
 * Shown in the team detail area when the signed-in user is NOT a member of the
 * team they navigated to (e.g. via a shared deep link). Team data is gated
 * server-side (every endpoint 403s for non-members); this is the matching
 * client state so a non-member sees a clear "no access" message instead of a
 * blank page. No team content is shown — not even the team name, which the
 * client doesn't have for a non-member.
 */
export function TeamAccessDenied({ onBack, onJoinWithCode }: TeamAccessDeniedProps) {
  return (
    <div
      role="alert"
      aria-label="No access to this team"
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', gap: 14, padding: '48px 24px', maxWidth: 520, margin: '64px auto',
        border: `1px solid ${COLORS.surfaceContainerHighest}`, borderRadius: 14, background: COLORS.surface,
      }}
    >
      <div
        style={{
          width: 56, height: 56, borderRadius: 999, background: COLORS.primaryContainer,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Icon name="shield" size={26} color={COLORS.primary} />
      </div>
      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>You don&apos;t have access to this team</h2>
      <p style={{ margin: 0, fontSize: 14, color: COLORS.outline, lineHeight: 1.5 }}>
        This team is private to its members. Its hunts, members, documents, map and pictures are only
        visible to people who belong to the team. Ask a team admin to invite you, or join with a team code.
      </p>
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        {onBack && (
          <button className="wh-btn wh-btn-outline" type="button" onClick={onBack}>
            Back to teams
          </button>
        )}
        {onJoinWithCode && (
          <button className="wh-btn wh-btn-primary" type="button" onClick={onJoinWithCode}>
            Join with a code
          </button>
        )}
      </div>
    </div>
  )
}
